import React, { useState } from "react";
import { KPI_FRAMEWORK } from "../lib/data";
import { KPI_DETAIL, safeKpiTrend } from "../lib/dataKpi";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import KpiDrilldown from "../components/KpiDrilldown";
import { formatPeso, pct, cn } from "../lib/utils";
import { useRole } from "../lib/role.jsx";
import { useChartColors } from "../lib/chartColors";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { ArrowRight, TrendingUp, TrendingDown, Minus, Target } from "lucide-react";

function formatKPI(v, format) {
  if (v === undefined || v === null) return "—";
  if (format === "peso") return formatPeso(v, { compact: true });
  if (format === "pct" || format === "share") return pct(v);
  if (format === "x") return `${(+v).toFixed(1)}×`;
  if (format === "days") return `${v} days`;
  return v;
}

export default function KpiFramework() {
  const [selectedKpi, setSelectedKpi] = useState(null);
  const { kpiCategories, role } = useRole();

  if (selectedKpi) {
    return <KpiDrilldown kpi={selectedKpi} onBack={() => setSelectedKpi(null)} />;
  }

  // Filter KPI_FRAMEWORK by allowed categories. null = all categories allowed.
  const visibleKpis = kpiCategories === null
    ? KPI_FRAMEWORK
    : KPI_FRAMEWORK.filter(k => kpiCategories.includes(k.category));

  const grouped = visibleKpis.reduce((acc, k) => {
    (acc[k.category] = acc[k.category] || []).push(k);
    return acc;
  }, {});
  const cats = Object.keys(grouped);
  const isScoped = kpiCategories !== null;

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART IX · Governance" title="1Rotary KPI Framework"
        description="Single source of truth for executive KPIs — definition, target, current actual, 24-month trend and AI explanation per category. Click any KPI to drill down." />

      {isScoped && (
        <div data-testid="kpi-scope-banner" className="tile p-3 flex items-center gap-2.5 text-[12px] border-l-2 border-primary/60 bg-primary/5">
          <Badge status="info">Role-scoped</Badge>
          <span className="text-fg/90">Showing {visibleKpis.length} of {KPI_FRAMEWORK.length} KPIs — categories visible to <span className="font-medium">{role?.name}</span>: {(kpiCategories || []).join(", ") || "—"}.</span>
        </div>
      )}

      <div className="tile p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 text-sm">
          {cats.map(c => (
            <a key={c} href={`#kpi-${c}`} className="flex items-center justify-between border-b border-border py-1.5 hover:text-primary" data-testid={`kpi-cat-link-${c}`}>
              <span className="font-display font-medium">{c}</span>
              <span className="font-mono-tabular text-muted">{grouped[c].length}</span>
            </a>
          ))}
        </div>
      </div>

      {cats.map(c => (
        <div key={c} id={`kpi-${c}`} className="space-y-3">
          <div className="flex items-end justify-between">
            <h3 className="font-display text-xl font-semibold tracking-tight">{c}</h3>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted">{grouped[c].length} KPI{grouped[c].length > 1 ? "s" : ""}</span>
          </div>
          <Tile dense testid={`kpi-cat-${c}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-muted">
                    <th className="text-left py-2 px-4 border-b border-border w-[24%]">KPI</th>
                    <th className="text-left py-2 px-4 border-b border-border">Definition</th>
                    <th className="text-left py-2 px-4 border-b border-border w-[12%]">Target</th>
                    <th className="text-right py-2 px-4 border-b border-border w-[10%]">Actual</th>
                    <th className="text-center py-2 px-4 border-b border-border w-[14%]">24M Trend</th>
                    <th className="text-center py-2 px-4 border-b border-border w-[10%]">Status</th>
                    <th className="text-center py-2 px-4 border-b border-border w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[c].map((k) => {
                    const detail = KPI_DETAIL[k.name];
                    const trend = detail?.trend || safeKpiTrend(k.name, k.actual);
                    const first = trend[0]?.value;
                    const last = trend[trend.length - 1]?.value;
                    const trendDelta = first ? (last - first) / first : 0;
                    const trendIsUp = trendDelta > 0.01;
                    const trendIsDown = trendDelta < -0.01;
                    return (
                      <tr key={k.name}
                          data-testid={`kpi-row-${k.name}`}
                          onClick={() => setSelectedKpi(k)}
                          className="border-b border-border last:border-b-0 hover:bg-surface-2 cursor-pointer transition-colors group">
                        <td className="py-3 px-4"><div className="font-display font-semibold">{k.name}</div></td>
                        <td className="py-3 px-4 text-muted text-xs leading-relaxed">{k.definition}</td>
                        <td className="py-3 px-4 text-xs text-fg/85 font-mono-tabular">{k.target}</td>
                        <td className="py-3 px-4 text-right font-mono-tabular font-semibold">{formatKPI(k.actual, k.format)}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 justify-center">
                            <InlineSparkline data={trend} good={trendIsUp} bad={trendIsDown} />
                            <span className={cn("text-[10px] font-mono-tabular font-semibold inline-flex items-center gap-0.5",
                              trendIsUp ? "text-success" : trendIsDown ? "text-critical" : "text-muted")}>
                              {trendIsUp ? <TrendingUp size={10} /> : trendIsDown ? <TrendingDown size={10} /> : <Minus size={10} />}
                              {trendDelta >= 0 ? "+" : ""}{(trendDelta * 100).toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center"><Badge status={k.status}>{k.status}</Badge></td>
                        <td className="py-3 px-4 text-center">
                          <ArrowRight size={14} className="text-muted opacity-60 group-hover:opacity-100 group-hover:text-primary mx-auto" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Tile>
        </div>
      ))}

      <Tile title="Governance Note">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm leading-relaxed">
          <div>
            <div className="font-display font-semibold mb-1.5">Tier 1 — Executive Data Council</div>
            <p className="text-muted text-[13px]">Chaired by CEO; members: VP Ops, Demand Planning, Merchandising, CFO. Monthly cadence. Approves KPI definitions, data quality standards, AI performance thresholds.</p>
          </div>
          <div>
            <div className="font-display font-semibold mb-1.5">Tier 2 — Functional Data Owners</div>
            <p className="text-muted text-[13px]">Demand Planning owns inventory & demand · Merchandising owns product & supplier · Marketing owns online channel · Finance owns financial data. Monthly reviews enforce data accuracy.</p>
          </div>
        </div>
      </Tile>
    </div>
  );
}

/* ---------------- Inline 24-pt sparkline ---------------- */
function InlineSparkline({ data, good, bad }) {
  const C = useChartColors();
  const color = good ? C.success : bad ? C.critical : C.muted;
  return (
    <div style={{ width: 96, height: 28 }} data-testid="kpi-sparkline">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 4, bottom: 2, left: 0, right: 0 }}>
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

